import React from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Transition } from 'react-transition-group';
import Scrollspy from 'react-scrollspy';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';

// Components
import Heading from '../Heading';
import CategoryNavItem from '../CategoryNavItem';
import AppLink from '../AppLink';

// Icons
import CheckIcon from '../../icons/done.svg';
import GiftIcon from '../../icons/gift.svg';

// Router
import { getRoute } from '../../entry/routes';

// State
import {
  getConditions,
  getRewards,
  progressSelector,
  allConditionsCheckedSelector,
  checkedConditionsSelector,
} from '../../state/modules/stock/selectors';

// Styles
import styles from './CategoryNavMobile.styl';

const cx = classNames.bind(styles);

export default function CategoryNavMobile(props) {
  const { className } = props;

  const isHomeCategoriesStick = useSelector(state => state.ui.isHomeCategoriesStick);
  const categoriesAliases = useSelector(state => state.catalog.products.categoriesAliases);
  const code = useSelector(state => state.stock.data.code) || '';
  const isApplied = useSelector(state => state.stock.isApplied);
  const conditionsChecked = useSelector(checkedConditionsSelector);
  const allConditionsChecked = useSelector(allConditionsCheckedSelector);
  const progress = useSelector(progressSelector);
  const rewards = useSelector(getRewards);
  const conditions = useSelector(getConditions);

  const progressStyles = { transform: `scaleX(${progress})` };

  return Object.keys(categoriesAliases).length > 0 ? (
    <Transition
      in={isHomeCategoriesStick}
      timeout={{ enter: 0, exit: 100 }}
      mountOnEnter
      unmountOnExit
    >
      {transitionState => (
        <div className={cx('CategoryNavMobile', `CategoryNavMobile_${transitionState}`, className)}>
          <Scrollspy
            className={cx('CategoryNavMobile__list')}
            currentClassName={cx('CategoryNavMobile__item_is-current')}
            offset={-50}
            componentTag="div"
            items={Object.keys(categoriesAliases)}
          >
            {Object.keys(categoriesAliases).map(alias => (
              <CategoryNavItem
                key={alias}
                role="button"
                tabIndex="0"
                alias={alias}
                className={cx('CategoryNavMobile__item')}
              >
                <Heading className={cx('CategoryNavMobile__item-name')} level={5}>
                  {categoriesAliases[alias]}
                </Heading>
              </CategoryNavItem>
            ))}
          </Scrollspy>
          {isApplied && (
            <div className={cx('CategoryNavMobile__promocode')}>
              {!allConditionsChecked ? (
                <>
                  <div className={cx('CategoryNavMobile__promocode-info')}>
                    <FormattedMessage id="promocode.title">
                      {title => (
                        <Heading
                          className={cx('CategoryNavMobile__promocode-code')}
                          tagName="div"
                          level={5}
                        >
                          {title}: {code}
                        </Heading>
                      )}
                    </FormattedMessage>
                    <Heading
                      className={cx('CategoryNavMobile__promocode-count')}
                      tagName="div"
                      level={5}
                    >
                      {conditionsChecked.length} / {conditions.length}
                    </Heading>
                  </div>
                  <div className={cx('CategoryNavMobile__promocode-progress')}>
                    <div
                      className={cx('CategoryNavMobile__promocode-progress-scale')}
                      style={progressStyles}
                    />
                  </div>
                </>
              ) : (
                <>
                  {rewards.map((reward, index) =>
                    reward.applied ? (
                      <Heading
                        key={index}
                        tagName="div"
                        level={5}
                        className={cx('CategoryNavMobile__promocode-reward')}
                      >
                        {reward.message}
                        <span className={cx('CategoryNavMobile__promocode-reward-done')}>
                          <CheckIcon width={16} height={16} />
                        </span>
                      </Heading>
                    ) : (
                      <Heading
                        to={getRoute('stockGifts', index, code)}
                        key={index}
                        tagName={AppLink}
                        level={5}
                        className={cx('CategoryNavMobile__promocode-reward')}
                      >
                        {reward.message}
                        <span className={cx('CategoryNavMobile__promocode-reward-done')}>
                          <GiftIcon width={16} height={16} />
                        </span>
                      </Heading>
                    ),
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </Transition>
  ) : null;
}

CategoryNavMobile.defaultProps = {
  className: '',
};

CategoryNavMobile.propTypes = {
  className: PropTypes.string,
};
